import { useState, useEffect } from 'react';

const useColorExtractor = (imageSrc) => {
  const [dominantColor, setDominantColor] = useState('#ffffff');

  useEffect(() => {
    if (!imageSrc) return;

    const extractColor = async () => {
      try {
        // Create a canvas to analyze the image
        const img = new Image();
        img.crossOrigin = 'anonymous';

        img.onload = () => {
          const canvas = document.createElement('canvas');
          const ctx = canvas.getContext('2d');

          // Set canvas size to a smaller version for performance
          const maxSize = 100;
          const ratio = Math.min(maxSize / img.width, maxSize / img.height);
          canvas.width = img.width * ratio;
          canvas.height = img.height * ratio;

          // Draw and analyze the image
          ctx.drawImage(img, 0, 0, canvas.width, canvas.height);
          const imageData = ctx.getImageData(0, 0, canvas.width, canvas.height);
          const data = imageData.data;

          // Simple color averaging - take samples from different parts of the image
          let r = 0, g = 0, b = 0, count = 0;

          // Sample pixels in a grid pattern for better performance
          const step = Math.max(1, Math.floor(canvas.width / 10));

          for (let y = 0; y < canvas.height; y += step) {
            for (let x = 0; x < canvas.width; x += step) {
              const index = (y * canvas.width + x) * 4;
              r += data[index];
              g += data[index + 1];
              b += data[index + 2];
              count++;
            }
          }

          // Calculate average color
          r = Math.round(r / count);
          g = Math.round(g / count);
          b = Math.round(b / count);

          // Convert to HSL and adjust saturation/brightness for better background
          const hsl = rgbToHsl(r, g, b);

          // Increase saturation and slightly adjust lightness for better contrast
          hsl[1] = Math.min(1, hsl[1] * 1.2); // Increase saturation
          hsl[2] = Math.max(0.1, Math.min(0.9, hsl[2])); // Adjust lightness

          const rgb = hslToRgb(hsl[0], hsl[1], hsl[2]);
          const colorString = `rgb(${rgb[0]}, ${rgb[1]}, ${rgb[2]})`;

          setDominantColor(colorString);
        };

        img.src = imageSrc;
      } catch (error) {
        console.warn('Failed to extract color from image:', error);
        setDominantColor('#f5f5f5'); // Fallback color
      }
    };

    extractColor();
  }, [imageSrc]);

  return dominantColor;
};

// Helper functions for color conversion
function rgbToHsl(r, g, b) {
  r /= 255;
  g /= 255;
  b /= 255;

  const max = Math.max(r, g, b);
  const min = Math.min(r, g, b);
  let h, s, l = (max + min) / 2;

  if (max === min) {
    h = s = 0; // achromatic
  } else {
    const d = max - min;
    s = l > 0.5 ? d / (2 - max - min) : d / (max + min);

    switch (max) {
      case r: h = (g - b) / d + (g < b ? 6 : 0); break;
      case g: h = (b - r) / d + 2; break;
      case b: h = (r - g) / d + 4; break;
      default: h = 0;
    }
    h /= 6;
  }

  return [h, s, l];
}

function hslToRgb(h, s, l) {
  let r, g, b;

  if (s === 0) {
    r = g = b = l; // achromatic
  } else {
    const hue2rgb = (p, q, t) => {
      if (t < 0) t += 1;
      if (t > 1) t -= 1;
      if (t < 1/6) return p + (q - p) * 6 * t;
      if (t < 1/2) return q;
      if (t < 2/3) return p + (q - p) * (2/3 - t) * 6;
      return p;
    };

    const q = l < 0.5 ? l * (1 + s) : l + s - l * s;
    const p = 2 * l - q;

    r = hue2rgb(p, q, h + 1/3);
    g = hue2rgb(p, q, h);
    b = hue2rgb(p, q, h - 1/3);
  }

  return [Math.round(r * 255), Math.round(g * 255), Math.round(b * 255)];
}

export default useColorExtractor;

